import { CommonModule } from "@angular/common";
import { Component, computed, inject, OnInit, signal, TemplateRef, ViewChild } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import {
  AccessLevel,
  AccountState,
  Currency,
  Customer,
  DailyAdSpendFee,
  Invoice,
  InvoiceScheduling,
  KeywordTrackerQuota,
  Organization,
  Plan,
  Response,
} from "@front/m19-api-client";
import { COUNTRIES, OrganizationAccountGroups } from "@front/m19-models";
import {
  arrayGroupBy,
  AuthService,
  BillingService,
  KeywordTrackingService,
  OrganizationService,
  StatsService,
  UpgradePlanService,
  UserSelectionService,
} from "@front/m19-services";
import {
  BillingInfoComponent,
  BillingInfoFormComponent,
  BillingInfoFormValue,
  CreditCardsComponent,
  IButtonComponent,
  IInputComponent,
  InvoicesTableComponent,
  ISelectComponent,
  LinkStripeCardModalComponent,
  ModalRef,
  ModalService,
  Option,
  PlanCardComponent,
  SpinnerComponent,
  ToasterService,
} from "@front/m19-ui";
import { Utils } from "@front/m19-utils";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { OnboardingCtaComponent } from "@m19-board/containers/board-layout/board-sidebar/onboarding-cta.component";
import { BoardType, LayoutSelectorService } from "@m19-board/layout-selector.service";
import {
  SelfServiceTransferModalComponent,
  SelfServiceTransferModalData,
} from "@m19-board/settings/billing-settings/self-service-transfer/self-service-transfer.component";
import { UntilDestroy } from "@ngneat/until-destroy";
import { environment } from "apps/m19-board/src/environments/environment";
import { ExportToCsv } from "export-to-csv";
import moment from "moment";
import { Observable } from "rxjs";
import { AjaxError } from "rxjs/ajax";
import { filter, map, switchMap, tap } from "rxjs/operators";
import { SettingStatus } from "../../models/Billing";
import {
  InvoiceDetailsModalComponent,
  InvoiceDetailsModalData,
} from "./invoice-details-modal/invoice-details-modal.component";

@UntilDestroy()
@Component({
  selector: "app-billing-settings",
  templateUrl: "billing-settings.component.html",
  standalone: true,
  imports: [
    TranslocoDirective,
    IButtonComponent,
    OnboardingCtaComponent,
    BillingInfoFormComponent,
    PlanCardComponent,
    IInputComponent,
    CreditCardsComponent,
    InvoicesTableComponent,
    BillingInfoComponent,
    SpinnerComponent,
    ISelectComponent,
    CommonModule,
  ],
})
export class BillingSettingsComponent implements OnInit {
  private readonly authService = inject(AuthService);
  private readonly billingService = inject(BillingService);
  private readonly modalService = inject(ModalService);
  private readonly statsService = inject(StatsService);
  private readonly layoutSelector = inject(LayoutSelectorService);
  private readonly keywordTrackerService = inject(KeywordTrackingService);
  private readonly toasterService = inject(ToasterService);
  private readonly translocoService = inject(TranslocoService);
  private readonly organizationService = inject(OrganizationService);
  private readonly upgradePlanService = inject(UpgradePlanService);
  private readonly userSelectionService = inject(UserSelectionService);

  private readonly organizationsGroups$ = this.billingService.organizations$.pipe(
    map((organizations: OrganizationAccountGroups[]) => {
      return this.whiteLabel
        ? organizations.filter(
            (x) =>
              x.isParentPpcBoard ||
              (x.hasSubscription() &&
                x.accountGroups
                  .flatMap((ag) => ag.getAccountMarketplaces())
                  .filter((am) => am.state == AccountState.BIDDER_ON).length > 0),
          )
        : organizations;
    }),
    tap((organizations) => {
      this.initDailyHourlyKeywordMap(organizations);
    }),
  );

  private organizations = toSignal(this.organizationsGroups$);

  organizationOptions = computed<Option<OrganizationAccountGroups>[]>(
    () =>
      this.organizations()?.map((organization) => ({
        label: organization.organizationName,
        value: organization,
      })) ?? [],
  );
  nextInvoice = signal<InvoiceScheduling | undefined>(undefined);

  selectedOrganization = signal<OrganizationAccountGroups | undefined>(undefined);

  poNumberLoading = signal(false);
  poNumber = signal<string | undefined>(undefined);
  poNumberInput = signal<string | undefined>(undefined);

  customerLoading = signal(false);

  @ViewChild("updatePoNumberModal") updatePoNumberModal!: TemplateRef<any>;
  updatePoNumberModalRef: ModalRef<any> | undefined;

  @ViewChild("updateBillingDetailsModal") updateBillingDetailsModal!: TemplateRef<BillingInfoFormComponent>;
  modalRef: ModalRef<void, TemplateRef<BillingInfoFormComponent>> | undefined;

  customerCountry = computed(() => COUNTRIES.find((c) => c.code === this.selectedOrganization()!.customer?.country));

  stripePublishableKey = environment.stripePublicKey;

  user = toSignal(this.authService.loggedUser$);
  locale = computed(() => this.user()?.locale);
  currency = toSignal(this.userSelectionService.selectedCurrency$);
  isOrgOwner = computed(() => this.selectedOrganization()!.organization.ownerId == this.user()?.userId);
  canDisplayBillingInfo = computed(() => {
    return (
      !this.isSelfService() &&
      ((this.selectedOrganization()!.isParentPpcBoard && this.isOrgOwner()) || !this.whiteLabel)
    );
  });

  whiteLabel = this.layoutSelector.getBoardType() !== BoardType.M19;

  organizationKeywordMap: Map<number, Map<string, KeywordTrackerQuota>> = new Map();

  accountMarketplaces = computed(() =>
    this.selectedOrganization()!
      .accountGroups.flatMap((x) => x.resources)
      .filter((x) => x.state == AccountState.BIDDER_ON),
  );
  plan = computed(() => this.selectedOrganization()!.organization.billingPlan?.plan);
  canMigrateToParentOrganization = computed(
    () =>
      this.plan() === Plan.SELF_SERVICE &&
      !!this.selectedOrganization()?.parentOrganizationWhenOnwer &&
      this.accountMarketplaces() &&
      this.accountMarketplaces().length > 0,
  );

  isSelfService = computed(() => this.plan() === Plan.SELF_SERVICE);

  parentOrgName = computed(() => {
    return this.selectedOrganization()!.parentOrganizationWhenOnwer?.customerName;
  });

  ngOnInit(): void {
    this.organizationsGroups$
      .pipe(
        filter((organizations) => organizations.length > 0),
        tap((organizations) => {
          this.selectedOrganization.set(organizations[0]);
          this.poNumber.set(organizations[0].organization.poNumber ?? undefined);
        }),
        switchMap((organizations) => this.billingService.getInvoiceScheduling(organizations[0].customerId)),
      )
      .subscribe({
        next: (response) => {
          this.nextInvoice.set(response);
        },
      });
  }

  private dailyAdSpendFees = toSignal(
    this.statsService.getDailyAdSpendFee().pipe(
      map((data: DailyAdSpendFee[]) => {
        return arrayGroupBy(data, (x: DailyAdSpendFee) => x.parentOrganizationId ?? x.organizationId!);
      }),
    ),
  );

  totalOrganizationAdSpendFees = computed(() => {
    const data: DailyAdSpendFee[] =
      this.dailyAdSpendFees()?.get(this.selectedOrganization()!.organization.organizationId!) ?? [];

    const fees =
      data?.filter((x) => !x.stripeInvoiceId && Utils.toMoment(x.date!).isAfter(moment.utc().subtract(1, "month"))) ??
      [];

    return fees.reduce(function (r, a) {
      if (!a || !a.adSpendFee) return r;
      const key = a.accountId + "_" + a.marketplace;
      if (r.has(key)) {
        r.get(key)!.adSpendFee = r.get(key)!.adSpendFee! + a.adSpendFee;
      } else {
        r.set(key, a);
      }
      return r;
    }, new Map<string, DailyAdSpendFee>());
  });

  feesByInvoiceId = computed(() =>
    arrayGroupBy(
      this.dailyAdSpendFees()?.get(this.selectedOrganization()!.organization.organizationId!) ?? [],
      (fee: DailyAdSpendFee) => fee.stripeInvoiceId!,
    ),
  );

  readonly SettingStatus = SettingStatus;

  initDailyHourlyKeywordMap(organizations: OrganizationAccountGroups[]) {
    this.organizationKeywordMap = new Map();
    for (const organisation of organizations) {
      if (organisation.accessLevel !== AccessLevel.ADMIN) {
        continue;
      }
      this.keywordTrackerService.getDailyHourlyKeywordNumber(organisation.id).subscribe({
        next: (data) => {
          this.organizationKeywordMap.set(
            organisation.id,
            new Map(
              data.map((x: KeywordTrackerQuota) => {
                return [x.accountId?.toString() + "_" + x.marketplace?.toString() + "_" + x.frequency, x];
              }),
            ),
          );
        },
        error: (err) => {
          this.toasterService.error(err);
        },
      });
    }
  }

  openUpdatePoNumberModal() {
    this.updatePoNumberModalRef = this.modalService.openModal(this.updatePoNumberModal, {
      modalTitle: this.translocoService.translate("subscription-card.po_number"),
    });
  }

  updatePoNumberModalRefClose() {
    this.updatePoNumberModalRef?.close();
  }

  updatePoNumber(deletePoNumber?: boolean) {
    this.poNumberLoading.set(true);
    const poNumber = deletePoNumber ? "null" : this.poNumberInput();

    this.organizationService.updatePoNumber(
      this.selectedOrganization()!.organization.organizationId!,
      poNumber!,
      (res: Response) => {
        const updatedOrg = res.entity as Organization;
        this.poNumber.set(updatedOrg.poNumber);

        this.toasterService.success(
          this.translocoService.translate(
            deletePoNumber ? "subscription-card.po_number_deleted" : "subscription-card.po_number_updated",
          ),
        );
        this.poNumberLoading.set(false);
        this.updatePoNumberModalRefClose();
      },
      (error: AjaxError) => {
        this.toasterService.error(
          this.translocoService.translate("subscription-card.po_number_update_failed"),
          error.name ?? this.translocoService.translate("common.error"),
        );
        this.poNumberLoading.set(false);
      },
    );
  }

  openAddCreditCardModal() {
    this.modalService.openModal(LinkStripeCardModalComponent, {
      modalTitle: this.translocoService.translate("billing-settings.add_a_credit_card"),
      data: {
        stripePublicKey: environment.stripePublicKey,
        customer: this.selectedOrganization()!.customer!,
        user: this.user()!,
      },
    });
  }

  deleteCreditCard(cardId: string) {
    this.billingService.removeCreditCard(this.selectedOrganization()!.customer!, cardId).subscribe({
      complete: () => {
        this.toasterService.success(
          this.translocoService.translate("billing-registration-payment.credit_card_deleted"),
        );
      },
      error: (error: AjaxError) => {
        this.toasterService.error(
          error.response ? error.response.message : this.translocoService.translate("common.error"),
          error.name,
        );
      },
    });
  }

  setAsDefaultCreditCard(cardId: string) {
    this.billingService.linkCreditCard(this.selectedOrganization()!.customer!, cardId).subscribe({
      next: () => {
        this.toasterService.success(
          this.translocoService.translate("billing-registration-payment.default_credit_card_updated"),
        );
      },
      error: (error: AjaxError) => {
        this.toasterService.error(
          error.response ? error.response.message : this.translocoService.translate("common.error"),
          error.name,
        );
      },
    });
  }

  openUpdateBillingDetailsModal() {
    this.modalRef = this.modalService.openModal<void, void, TemplateRef<BillingInfoFormComponent>>(
      this.updateBillingDetailsModal,
      {
        modalTitle: this.translocoService.translate("billing-settings.update_billing_info"),
      },
    );
  }

  openMigrateToParentOrganizationModal() {
    if (!this.selectedOrganization()) return;
    this.modalService.openModal<SelfServiceTransferModalData, void>(SelfServiceTransferModalComponent, {
      modalTitle: this.translocoService.translate("self-service-transfer.transfer_self_service_to_agency_management"),
      data: {
        childOrganization: this.selectedOrganization()!.organization,
        accountMarketplaces: this.accountMarketplaces(),
      },
    });
  }

  updateBillingDetails(billingFormValue: BillingInfoFormValue) {
    const customer: Customer = {
      ...(this.selectedOrganization()!.customer! || {}),
      ...{ ...billingFormValue, paymentId: undefined },
      m19Email: this.user()!.email,
      m19UserId: this.user()!.userId,
    };

    this.customerLoading.set(true);
    let createOrUpdateCustomer: Observable<Customer>;
    if (this.selectedOrganization()!.customer) {
      createOrUpdateCustomer = this.upgradePlanService.updateCustomer(customer);
    } else {
      createOrUpdateCustomer = this.upgradePlanService.createCustomer(customer);
    }

    createOrUpdateCustomer.subscribe({
      next: () => {
        this.modalRef?.close();
        this.toasterService.success(this.translocoService.translate("billing-settings.billing_details_updated"));
        this.customerLoading.set(false);
      },
      error: (error) => {
        this.toasterService.error(error.response ? error.response.message : error.message);
        this.customerLoading.set(false);
      },
    });
  }

  exportAccountSpendCsv(): void {
    const date = moment(moment.now());

    const options = {
      quoteStrings: "",
      fieldSeparator: ";",
      showLabels: true,
      useKeysAsHeaders: true,
      filename: "spending_details_" + date.format("L"),
    };

    const csvExporter = new ExportToCsv(options);

    const data = Array.from(this.totalOrganizationAdSpendFees().values()).map((acc: DailyAdSpendFee) => ({
      Account: acc.accountName && acc.accountName.length > 0 ? acc.accountName : acc.accountId,
      MarketPlace: acc.marketplace,
      TotalAdSpend: acc.adSpendFee,
    }));

    csvExporter.generateCsv(data);
  }

  openInvoiceDetailsModal(invoice: Invoice): void {
    const currency = invoice.currency?.toUpperCase() as Currency;
    if (!this.feesByInvoiceId().get(invoice.invoiceId!)) return;

    this.modalService.openModal<InvoiceDetailsModalData, void>(InvoiceDetailsModalComponent, {
      modalTitle: this.translocoService.translate("invoice-details-modal.invoice_details"),
      data: {
        date: invoice.created!,
        locale: this.locale()!,
        currency: currency!,
        dailyAdSpendFees: this.feesByInvoiceId().get(invoice.invoiceId!)!,
      },
    });
  }

  changeOrganization(organization: OrganizationAccountGroups) {
    this.selectedOrganization.set(organization);
    this.poNumber.set(organization.organization.poNumber ?? undefined);
  }
}
